import { ExternalLink, Sparkles } from "lucide-react";
import { Button, Notice } from "../../../components/ui";
import { MONTHLY_PRICE } from "../../billing";
import { FREE_BETA } from "../../../lib/featureFlags";

type SubscriptionSectionProps = {
  isSubscribed: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  subscribing: boolean;
  managing: boolean;
  error: string;
  onSubscribe: () => void;
  onManage: () => void;
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const SubscriptionSection = ({
  isSubscribed,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  subscribing,
  managing,
  error,
  onSubscribe,
  onManage,
}: SubscriptionSectionProps) => (
  <>
    {/* During the beta everyone can publish; someone who subscribed before
        it still has a subscription to manage. */}
    {FREE_BETA && !currentPeriodEnd ? (
      <p className="text-sm leading-6 text-hazumu-muted">
        Every feature is free while Hazumu is in beta. After launch, designing
        your site stays free, and keeping it live is {MONTHLY_PRICE} a month.
        We’ll give you thirty days’ notice first.
      </p>
    ) : isSubscribed ? (
      <div className="space-y-4">
        <p className="text-sm leading-6 text-hazumu-muted">
          {cancelAtPeriodEnd && currentPeriodEnd
            ? `Your subscription is cancelled — it stays active until ${formatDate(currentPeriodEnd)}, and published sites remain online until then.`
            : `Your subscription is active. Published sites stay online while your subscription is active.`}
        </p>
        <Button
          disabled={managing}
          fullWidth
          onClick={onManage}
          size="lg"
          variant="secondary"
        >
          {managing ? "Opening portal…" : "Manage subscription"}
          <ExternalLink size={16} />
        </Button>
      </div>
    ) : (
      <div className="space-y-4">
        <p className="text-sm leading-6 text-hazumu-muted">
          Designing your site is free. To put it live and keep it online, it’s
          {` ${MONTHLY_PRICE}`} a month. Cancel anytime.
        </p>
        <Button
          disabled={subscribing}
          fullWidth
          onClick={onSubscribe}
          size="lg"
          variant="accent"
        >
          <Sparkles size={16} />
          {subscribing ? "Opening checkout…" : "Go live"}
        </Button>
        <Button
          disabled={managing || subscribing}
          fullWidth
          onClick={onManage}
          size="sm"
          variant="ghost"
        >
          {managing ? "Opening portal…" : "Manage an existing subscription"}
        </Button>
      </div>
    )}
    {error && <Notice className="mt-4">{error}</Notice>}
  </>
);
